
import { useSearchParams } from 'react-router-dom';

export type CategoryType = 'get_live_categories' | 'get_vod_categories' | 'get_series_categories';
export type StreamAction = 'get_live_streams' | 'get_vod_streams' | 'get_series';

interface CategoryParams {
    type: CategoryType;
    categoryId: string | null;
    streamAction: StreamAction;
    seriesId: string | null;
}

interface CategoryParamsActions {
    setType: (type: CategoryType) => void;
    setCategoryId: (categoryId: string | null) => void;
    clearCategoryId: () => void;
    navigateToCategory: (categoryId: string) => void;
    setSeriesId: (id: string) => void;
    clearSeriesId: () => void;
}

/**
 * Custom hook to manage category-related query parameters
 *
 * @returns Object containing current params and helper functions to update them
 */
export const useCategoryParams = (): [CategoryParams, CategoryParamsActions] => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Get current values from URL
    const type = (searchParams.get('type') as CategoryType) || 'get_live_categories';
    const categoryId = searchParams.get('category_id');
    const seriesId = searchParams.get('series_id');

    // Map type to stream action
    const getStreamAction = (categoryType: CategoryType): StreamAction => {
        switch (categoryType) {
            case 'get_live_categories':
                return 'get_live_streams';
            case 'get_vod_categories':
                return 'get_vod_streams';
            case 'get_series_categories':
                return 'get_series';
            default:
                return 'get_live_streams';
        }
    };

    const params: CategoryParams = {
        type,
        categoryId,
        seriesId,
        streamAction: getStreamAction(type)
    };

    const actions: CategoryParamsActions = {
        setType: (type: string) => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('type', type);
            setSearchParams(newParams);
        },
        setCategoryId: (id: string | null) => {
            if (id) {
                const newParams = new URLSearchParams(searchParams);
                newParams.set('category_id', id);
                setSearchParams(newParams);
            }
        },
        clearCategoryId: () => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('category_id');
            newParams.delete('series_id'); // Clear series_id when clearing category
            setSearchParams(newParams);
        },
        setSeriesId: (id: string) => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('series_id', id);
            setSearchParams(newParams);
        },
        clearSeriesId: () => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('series_id');
            setSearchParams(newParams);
        },
        navigateToCategory: (newCategoryId: string) => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('category_id', newCategoryId);
            setSearchParams(newParams);
        }
    };

    return [params, actions];
};